import React from 'react';
import { Link } from 'react-router-dom';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-[#1a237e] p-6">
          <h1 className="text-2xl font-bold text-white">Terms of Service</h1>
          <p className="mt-2 text-indigo-200">Last updated: April 2025</p>
        </div>
        
        <div className="p-6 space-y-6">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
            <p className="text-gray-700">
              By accessing or using the Amalie AI language learning platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of the terms, you may not access the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Description of Service</h2>
            <p className="text-gray-700">
              Amalie AI provides an AI-powered language learning platform that includes interactive lessons, real-time feedback, personalized practice, and various learning tools. The Service may change from time to time without prior notice.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. User Accounts</h2>
            <p className="text-gray-700 mb-3">
              To use certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your account and password</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Subscription and Payments</h2>
            <p className="text-gray-700 mb-3">
              Some aspects of the Service require payment of fees. When purchasing a subscription or making a payment:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>You agree to pay all fees in accordance with the pricing and payment terms in effect at the time</li>
              <li>Subscription fees are billed in advance and are non-refundable</li>
              <li>You authorize us to charge your chosen payment method for all fees</li>
              <li>Subscriptions automatically renew unless canceled before the renewal date</li>
              <li>You can manage and cancel your subscription through your account settings</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Wallet and Transactions</h2>
            <p className="text-gray-700 mb-3">
              Our platform includes a digital wallet feature that allows you to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Add funds to your wallet</li>
              <li>Purchase digital items and services</li>
              <li>Transfer funds to other users</li>
              <li>Withdraw funds subject to our withdrawal policies</li>
            </ul>
            <p className="text-gray-700 mt-3">
              All transactions are final and non-refundable unless required by law. We reserve the right to investigate and reverse transactions in cases of suspected fraud or violations of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Intellectual Property</h2>
            <p className="text-gray-700 mb-3">
              The Service and its original content, features, and functionality are owned by Amalie AI and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="text-gray-700">
              You may not copy, modify, distribute, sell, or lease any part of our Service or included software, nor may you reverse engineer or attempt to extract the source code of that software, unless laws prohibit these restrictions or you have our written permission.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. User Content</h2>
            <p className="text-gray-700 mb-3">
              Our Service allows you to post, link, store, share, and otherwise make available certain information, text, graphics, videos, or other material. By providing User Content, you:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, and distribute your content</li>
              <li>Represent and warrant that you own or have the necessary rights to post the content</li>
              <li>Agree that your content will not violate the rights of any third party or any law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Prohibited Uses</h2>
            <p className="text-gray-700 mb-3">
              You agree not to use the Service:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>In any way that violates any applicable law or regulation</li>
              <li>To harass, abuse, or harm another person</li>
              <li>To impersonate any person or entity</li>
              <li>To engage in any conduct that restricts or inhibits anyone's use of the Service</li>
              <li>To attempt to gain unauthorized access to the Service or related systems</li>
              <li>To use the Service for commercial purposes without our prior written consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Limitation of Liability</h2>
            <p className="text-gray-700">
              In no event shall Amalie AI, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Termination</h2>
            <p className="text-gray-700">
              We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use the Service will immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Changes to Terms</h2>
            <p className="text-gray-700">
              We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Contact Us</h2>
            <p className="text-gray-700">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Email:</strong> terms@amah.ai<br />
              <strong>Address:</strong> Amalie AI, Dominican Republic, District Nacional,
            </p>
          </section>

          <div className="pt-6 mt-6 border-t border-gray-200">
            <Link to="/" className="text-indigo-600 hover:text-indigo-800">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}